import { Component, Input, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { ForecastedUsageApiResponse, TenantUsageData } from 'src/app/app.types';
import { UsageAmountsService } from 'src/app/services/usage/usage-amounts.service';
import { convertToUtcDate } from 'src/util/date-helpers';
import { ICountCard } from '../count-card/count-card.component';

@Component({
    selector: 'downloads-chart',
    templateUrl: './downloads-chart.component.html',
    styleUrls: ['./downloads-chart.component.scss'],
    standalone: false
})
export class DownloadsChartComponent implements OnInit {
  endOfFirstRetention: Date | null = null;

  public set startDate(v : Date) {
    this._startDate = v;
    this.startDateChanged$.next(v);
  }

  public get startDate() : Date {
    return this._startDate;
  }

  _startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  earliestSelectionDate: Date = new Date(2024, 0, 1);
  latestSelectionDate: Date = new Date();

  downloadAllCard: ICountCard;
  downloadClipCard: ICountCard;
  downloadClipAudioCard: ICountCard;

  tenantIdChanged$ = new Subject<string>();
  startDateChanged$ = new Subject<Date>();

  isShowingCurrentMonth?: boolean;

  @Input()
  public set tenantId(v : string) {
    this.tenantIdChanged$.next(v);
  }

  @Input()
  public set initialTenantData(v : TenantUsageData) {
    console.log('Initial tenant data set on downloads-chart:', v);
    if(v) {
      this.endOfFirstRetention = v.endOfFirstRetention;
      this.earliestSelectionDate = this._startDate;
      if(v.customer?.subscriptionStartDate && new Date(v.customer?.subscriptionStartDate).getTime() < this.earliestSelectionDate.getTime()) {
        const subStartDate = new Date(v.customer.subscriptionStartDate);
        this.earliestSelectionDate = new Date(subStartDate.getFullYear(), subStartDate.getMonth(), 1);
      }
      this.setData(v.usage, this._startDate);
    }
  }
  
  constructor(private usageAmountsService: UsageAmountsService) { }
  
  ngOnInit(): void {
    combineLatest([this.tenantIdChanged$, this.startDateChanged$]).pipe(
      filter(([tenantId, startDate]) => !!tenantId && !!startDate),
      switchMap(([tenantId, startDate]) => this.usageAmountsService.getForecast(tenantId,
        convertToUtcDate(startDate).toISOString(), 1, this.endOfFirstRetention))
    ).subscribe(usageData => {
      this.setData(usageData, this._startDate);
    });
  }

  changeToCurrentMonth() {
    this.startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }

  setData(usageData: ForecastedUsageApiResponse, date: Date) {
      const currentMonthKey = date.getFullYear() + '/' + (date.getMonth() + 1).toString().padStart(2, '0'); 
      const currentMonthData = usageData[currentMonthKey]?.actualUsage;
      this.downloadAllCard = {
        label: 'Download All (Entire Incident)',
        count: currentMonthData?.downloadAllInLastHourCount || 0
      };
      this.downloadClipCard = {
        label: 'Individual Clips',
        count: currentMonthData?.downloadClipInLastHourCount || 0
      };
      this.downloadClipAudioCard = {
        label: 'Audio Files',
        count: currentMonthData?.downloadClipMp3InLastHourCount || 0
      };
      this.isShowingCurrentMonth = new Date().getMonth() === this.startDate.getMonth() && new Date().getFullYear() === this.startDate.getFullYear();
    }
}
